import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import AgendaCalendarCore from "./AgendaCalendarCore";
import AgendaEventDialog from "./AgendaEventDialog";
import AgendaLegend from "./AgendaLegend";
import AgendaCalendarFilter from "./AgendaCalendarFilter";
import AgendaAvailabilityPanel from "./AgendaAvailabilityPanel";
import AgendaGoogleConnectPrompt from "./AgendaGoogleConnectPrompt";
import AgendaGoogleSyncButton from "./AgendaGoogleSyncButton";
import { useUserAgenda, useUserAgendaMutations } from "../../hooks/useUserAgenda";
import { useAgendaGoogleAutoSync } from "../../hooks/useAgendaGoogleAutoSync";
import { useActiveVideoRooms } from "../../hooks/useActiveVideoRooms";
import { parseManualAgendaEventId } from "../../utils/activeVideoUi";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { getTeamPaneContainerStyle } from "../Team/utils/teamUiUtils";
import { chatPath, teamTasksPaths } from "../../utils/teamNavigation";
import {
  AGENDA_SOURCE_KEYS,
  formatAgendaPeriodTitle,
  isAgendaCurrentPeriod,
  isAgendaEventVisible,
  mergeAgendaFeedEvent,
} from "./agendaUtils";
import { useTheme } from "@mui/material/styles";

dayjs.locale("pt-br");

const VIEW_MAP = {
  day: "timeGridDay",
  week: "timeGridWeek",
  month: "dayGridMonth",
  list: "listWeek",
};

const toggleSx = {
  textTransform: "none",
  fontWeight: 500,
  px: 1.5,
  py: 0.5,
  fontSize: "0.8125rem",
};

function defaultNewEventRange() {
  const start = new Date();
  const end = dayjs(start).add(1, "hour").toDate();
  return { start, end, allDay: false };
}

export default function AgendaPage({ basePath = "/equipe" }) {
  const { t } = useTranslation("teamAgenda");
  const theme = useTheme();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const containerStyle = useMemo(() => getTeamPaneContainerStyle(theme, false), [theme]);

  const [range, setRange] = useState(() => ({
    from: dayjs().startOf("month").subtract(7, "day").toDate(),
    to: dayjs().endOf("month").add(7, "day").toDate(),
  }));
  const [viewKey, setViewKey] = useState("month");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [defaultRange, setDefaultRange] = useState(null);
  const [dialogError, setDialogError] = useState("");
  const [videoWarning, setVideoWarning] = useState("");
  const [visibleSources, setVisibleSources] = useState(() => new Set(AGENDA_SOURCE_KEYS));
  const [selectedCalendarIds, setSelectedCalendarIds] = useState(null);

  const { data, isLoading, isError, error } = useUserAgenda({ from: range.from, to: range.to });
  useAgendaGoogleAutoSync();
  const { byAgendaEventId } = useActiveVideoRooms();
  const { createMutation, updateMutation, deleteMutation, createCalendarMutation, updateCalendarMutation, deleteCalendarMutation } = useUserAgendaMutations();
  const events = data?.events || [];
  const calendars = data?.calendars || [];
  const effectiveCalendarIds = useMemo(
    () =>
      selectedCalendarIds ||
      new Set(calendars.map((calendar) => Number(calendar.id)).filter((id) => Number.isFinite(id))),
    [selectedCalendarIds, calendars]
  );
  const defaultCalendarId = useMemo(() => {
    const selected = calendars.filter((calendar) => effectiveCalendarIds.has(Number(calendar.id)));
    return (
      selected.find((calendar) => calendar.canCreate || calendar.canManage) ||
      calendars.find((calendar) => calendar.canCreate || calendar.canManage)
    )?.id;
  }, [calendars, effectiveCalendarIds]);
  const filteredEvents = useMemo(
    () =>
      events
        .filter((ev) =>
          isAgendaEventVisible(ev, {
            visibleSources,
            calendarIds: effectiveCalendarIds,
          })
        )
        .map((ev) => {
          const eventId =
            Number(ev.meta?.eventId) ||
            parseManualAgendaEventId(ev.id) ||
            null;
          const activeVideo = eventId ? byAgendaEventId.get(eventId) : null;
          return activeVideo ? { ...ev, activeVideo } : ev;
        }),
    [events, visibleSources, effectiveCalendarIds, byAgendaEventId]
  );

  const periodTitle = useMemo(
    () => formatAgendaPeriodTitle(range.from, range.to, viewKey),
    [range.from, range.to, viewKey]
  );
  const isCurrentPeriod = useMemo(
    () => isAgendaCurrentPeriod(range.from, range.to, viewKey),
    [range.from, range.to, viewKey]
  );

  const handleDatesChange = useCallback(({ start, end }) => {
    setRange({ from: start, to: end });
  }, []);

  const handleViewChange = (_, next) => {
    if (!next) return;
    setViewKey(next);
    calendarRef.current?.changeView(VIEW_MAP[next]);
  };

  const handleToggleSource = useCallback((source) => {
    setVisibleSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  }, []);

  const handleToggleCalendar = useCallback(
    (calendarId) => {
      const id = Number(calendarId);
      if (!Number.isFinite(id)) return;
      setSelectedCalendarIds((prev) => {
        const base = prev || new Set(calendars.map((calendar) => Number(calendar.id)));
        const next = new Set(base);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [calendars]
  );

  const openCreate = useCallback((rangePayload) => {
    setSelectedEvent(null);
    setDefaultRange({ ...rangePayload, calendarId: rangePayload?.calendarId || defaultCalendarId });
    setDialogError("");
    setDialogOpen(true);
  }, [defaultCalendarId]);

  const openScheduleWith = useCallback(
    (person) => {
      const email = String(person?.email || "").trim().toLowerCase();
      if (!email) return;
      const name = String(person?.name || "").trim();
      openCreate({
        ...defaultNewEventRange(),
        title: name ? `Reunião com ${name}` : "",
        attendees: [{ email, ...(name ? { name } : {}) }],
      });
    },
    [openCreate]
  );

  const openDetails = useCallback((ev) => {
    setSelectedEvent(ev);
    setDefaultRange(null);
    setDialogError("");
    setDialogOpen(true);
  }, []);

  const handleEventClick = useCallback(
    (ev) => {
      if (!ev) return;
      if (ev.editable) {
        openDetails(ev);
        return;
      }
      if (ev.source === "next_contact" && ev.meta?.leadId) {
        navigate(chatPath(ev.meta.leadId));
        return;
      }
      if (ev.source === "task" && ev.meta?.boardId) {
        navigate(teamTasksPaths(basePath).board(ev.meta.boardId));
        return;
      }
      openDetails(ev);
    },
    [navigate, basePath, openDetails]
  );

  const handleSave = async (payload, eventId) => {
    setDialogError("");
    setVideoWarning("");
    try {
      const saved = eventId
        ? await updateMutation.mutateAsync({ eventId, payload })
        : await createMutation.mutateAsync(payload);
      setDialogOpen(false);
      const warn = saved?.meta?.videoWarning;
      if (warn?.message) {
        setVideoWarning(t("videoWarningBanner", { message: warn.message }));
      }
    } catch (err) {
      setDialogError(getApiErrorMessage(err, t("saveError")));
    }
  };

  const handleDelete = async (eventId) => {
    const isSeries = Boolean(selectedEvent?.meta?.recurring || selectedEvent?.meta?.recurrenceRule);
    if (!window.confirm(t(isSeries ? "recurrence.deleteSeriesConfirm" : "deleteConfirm"))) return;
    try {
      await deleteMutation.mutateAsync(eventId);
      setDialogOpen(false);
    } catch (err) {
      setDialogError(getApiErrorMessage(err, t("deleteError")));
    }
  };

  const handleStatusChange = async (eventId, status) => {
    setDialogError("");
    try {
      const updated = await updateMutation.mutateAsync({ eventId, payload: { status } });
      setSelectedEvent((prev) => mergeAgendaFeedEvent(prev, updated));
    } catch (err) {
      setDialogError(getApiErrorMessage(err, t("saveError")));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
        p: 1,
      }}
    >
      <Box
        sx={{
          ...containerStyle,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <IconButton
              size="small"
              onClick={() => navigate(basePath)}
              aria-label={t("back")}
              sx={{ mt: 0.25 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" lineHeight={1.25}>
                {t("title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("pageSubtitle")}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openCreate(defaultNewEventRange())}
              sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {t("newEvent")}
            </Button>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 2 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              size="small"
              variant={isCurrentPeriod ? "contained" : "outlined"}
              onClick={() => calendarRef.current?.today()}
              sx={{ textTransform: "none", fontWeight: 500, minWidth: 0 }}
            >
              {t("today")}
            </Button>

            <AgendaGoogleSyncButton />

            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewKey}
              onChange={handleViewChange}
              sx={{
                "& .MuiToggleButton-root": toggleSx,
                "& .Mui-selected": { fontWeight: 600 },
              }}
            >
              <ToggleButton value="day">{t("views.day")}</ToggleButton>
              <ToggleButton value="week">{t("views.week")}</ToggleButton>
              <ToggleButton value="month">{t("views.month")}</ToggleButton>
              <ToggleButton value="list">{t("views.list")}</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flex: 1, minWidth: 120 }} />

            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => calendarRef.current?.prev()}
                  aria-label={t("prevPeriod")}
                  sx={{ borderRadius: 0, p: 0.5 }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => calendarRef.current?.next()}
                  aria-label={t("nextPeriod")}
                  sx={{ borderRadius: 0, p: 0.5 }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                noWrap
                sx={{ textTransform: "capitalize", minWidth: 0 }}
              >
                {periodTitle}
              </Typography>
            </Stack>
          </Stack>

          <AgendaGoogleConnectPrompt />
          <AgendaCalendarFilter
            calendars={calendars}
            selectedCalendarIds={effectiveCalendarIds}
            onToggleCalendar={handleToggleCalendar}
            onCreateCalendar={(payload) => createCalendarMutation.mutateAsync(payload)}
            onUpdateCalendar={(args) => updateCalendarMutation.mutateAsync(args)}
            onDeleteCalendar={(calendarId) => deleteCalendarMutation.mutateAsync(calendarId)}
            creating={createCalendarMutation.isLoading}
            updating={updateCalendarMutation.isLoading}
            deleting={deleteCalendarMutation.isLoading}
          />
          <AgendaAvailabilityPanel range={range} compact={false} onScheduleWith={openScheduleWith} />
          <AgendaLegend selectedSources={visibleSources} onToggleSource={handleToggleSource} />
        </Box>

        <Divider />

        <Box sx={{ flex: 1, minHeight: 0, p: 1.5, pt: 1, position: "relative" }}>
          {videoWarning ? (
            <Alert severity="warning" onClose={() => setVideoWarning("")} sx={{ mb: 1 }}>
              {videoWarning}
            </Alert>
          ) : null}
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity="error">{getApiErrorMessage(error, t("loadError"))}</Alert>
          ) : (
            <AgendaCalendarCore
              ref={calendarRef}
              events={filteredEvents}
              initialView={VIEW_MAP[viewKey]}
              height="100%"
              viewKey={viewKey}
              headerToolbar={false}
              onDatesChange={handleDatesChange}
              onEventClick={handleEventClick}
              onDateClick={openCreate}
              onEventDrop={(id, p) => updateMutation.mutateAsync({ eventId: id, payload: p })}
              onEventResize={(id, p) => updateMutation.mutateAsync({ eventId: id, payload: p })}
            />
          )}
        </Box>
      </Box>

      <AgendaEventDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialEvent={selectedEvent}
        defaultRange={defaultRange}
        onSave={handleSave}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        saving={createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading}
        error={dialogError}
        calendars={calendars}
        defaultCalendarId={defaultCalendarId}
      />
    </Box>
  );
}

